import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { PermissionsService } from 'src/app/core/services/permissions/permission.service';
import {
  MyPurchaseForm,
  UserInformation,
} from 'src/app/shared/models/app-models';
import {
  TaggedGroup,
  TaggingValidationSuccess,
  TaggingValidationSuccessType,
  TagItemVendorService,
} from '../tag-item-vendor.service';
import { initialState } from 'src/app/core/store/form.reducer';
import _, { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import {
  SelectTableDialogComponent,
  SelectTableDialogData,
  SelectTableDialogResult,
  SelectTableDialogType,
} from '../select-table-dialog/select-table-dialog.component';
import {
  Item as ItemFromTable,
  SelectItemTableAction,
} from '../../../../shared/components/select-item-table/select-item-table.component';
import {
  Vendor as VendorFromTable,
  SelectVendorTableAction,
} from '../../../../shared/components/select-vendor-table/select-vendor-table.component';
import { IAddItem } from 'src/app/shared/models/app-models/MyPurchaseForm.model';
import { Vendor } from 'src/app/shared/models/database-models';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import {
  SourcingStatusId,
  SourcingStatusOption,
  SourcingStatusOptions,
} from 'src/app/shared/models/database-models/SourcingStatus';
import { VendorSearchDialogComponent, VendorSearchDialogData } from '../../item-vendor/add-vendor/vendor-search-dialog/vendor-search-dialog.component';
import { AslVendorSearchDialogComponent, AslVendorSearchDialogData } from '../../item-vendor/add-vendor/asl-vendor-search-dialog/asl-vendor-search-dialog.component';

enum HasVendorTag {
  YES = 'yes',
  NO = 'no',
}
enum VendorSearchType {
  NORMAL = 'normal',
  ASL = 'asl',
}

type ItemNumbers = IAddItem['displayItemNumber'][];
type NonNullVendorNumbers = NonNullable<Vendor['VendorNumber']>[];

@Component({
  selector: 'app-tagging-add',
  templateUrl: './tagging-add.component.html',
  styleUrls: ['./tagging-add.component.scss'],
})
export class TaggingAddComponent implements OnInit, OnDestroy {
  @Input() currentRoute = ['Purchase Modes'];
  isReadOnly: boolean = false; //TODO:
  permissionData$: Observable<any>;
  purchaseType: string = '';
  currentPageString: string = 'Tag Item-Vendor';
  destroy$: Subject<boolean> = new Subject<boolean>();
  form: MyPurchaseForm | null = null;
  public loggedInUsername?: string;
  private dialogSubscription?: Subscription;
  public sourcingStatusOptions = _.cloneDeep(SourcingStatusOptions);

  // Selected rows in tables
  public checkedItemNumbers: ItemNumbers = [];
  public checkedVendorNumbers: NonNullVendorNumbers = [];

  private docVendorsList?: MyPurchaseForm['VendorsToSource'];
  breadCrumbFlow:string[]=['Purchase Modes'];
  activePage: string = 'Tag Item-vendor';
  mpNumber: string = '';


  constructor(
    private permissionService: PermissionsService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{ user: UserInformation; form: MyPurchaseForm }>,
    public tagItemVendorService: TagItemVendorService,
    private dialog: MatDialog
  ) {
    if (sessionStorage.getItem('fromPage') === 'documentList') {
      this.breadCrumbFlow = ['My Documents'];
    } else if (sessionStorage.getItem('fromPage') === 'workList') {
      this.breadCrumbFlow = ['My Worklist'];
    }
    this.permissionData$ = permissionService
      .getService()
      .pipe(takeUntil(this.destroy$));
    this.purchaseType = this.route.snapshot.params['purchaseType'].split(
      ';'
    )[0] as string;
    this.mpNumber = this.route.snapshot.params['id'];
    this.setHeaderPath(this.purchaseType);

    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.loggedInUsername = user.userName;
      });

    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        // Redirect back to document page if other centralized form data is not loaded
        if (isEqual(form, initialState)) {
          this.router.navigate([
            '/purchase/',
            this.purchaseType,
            this.route.snapshot.params['id'],
          ]);
        }

        this.form = form;

        const newVendorsList = this.form.VendorsToSource;

        if (!_.isEqual(newVendorsList, this.docVendorsList)) {
          // Ignore first newVendorsList load
          if (this.docVendorsList) {
            this.selectNewDocVendors(newVendorsList);
          }

          this.docVendorsList = newVendorsList;
        }
      });

    this.tagItemVendorService.taggingValidationSuccess$
      .pipe(takeUntil(this.destroy$))
      .subscribe((validationSuccess: TaggingValidationSuccess) => {
        if (
          validationSuccess.type === TaggingValidationSuccessType.PASSED_ADD
        ) {
          this.tagItemVendorService.resetTempTagGroup();

          // Redirect back to main document page
          this.returnToRFQ();
        }
      });
  }

  ngOnInit() {
    // Scrolls to the top of the window
    window.scrollTo(0, 0);  
  }
  
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
  }

  // Automatically add newly added MPDocument vendors into temp tag group
  selectNewDocVendors(newVendorsList: Vendor[]) {
    const tempTagGroup = this.tagItemVendorService.tempTagGroupValue;
    const tagGroupVendors = tempTagGroup?.vendors;

    if (!Array.isArray(tagGroupVendors) || !this.docVendorsList) {
      return;
    }

    const existingVendorNumbers = this.docVendorsList
      .filter(
        (vendor) =>
          vendor.VendorNumber && !vendor.isDeleted && !vendor.Strikethrough
      )
      .map((vendor) => vendor.VendorNumber);

    const diffVendors = newVendorsList.filter(
      (vendor) =>
        vendor.VendorNumber &&
        !vendor.isDeleted &&
        !vendor.Strikethrough &&
        !existingVendorNumbers.includes(vendor.VendorNumber)
    );

    if (diffVendors.length > 0) {
      this.tagItemVendorService.updateTempTagGroup({
        vendors: [
          ...diffVendors.map((vendor) => ({
            vendorName: vendor.Name ?? '',
            vendorNumber: vendor.VendorNumber!,
          })),
          ...tagGroupVendors,
        ],
      });
    }
  }

  returnToRFQ = () => {
    this.router.navigate([
      '/purchase/',
      this.purchaseType,
      this.form?.documentNo,
      { tagged: true },
    ]);
  };

  async buttonHandler($event: any) {
    switch ($event.actionType as string) {
      case 'tag-item-vendor':
        this.addTaggedGroupAndReturnLater();
        break;
      default:
        // do nothing
        break;
    }
  }

  private addTaggedGroupAndReturnLater() {
    // Commit tempTagGroup to taggedGroups and reset tempTagGroup
    const tempTagGroup = this.tagItemVendorService.tempTagGroupValue!;

    // Asynchronously resolve validation issues and subscribe to handle validationSuccess
    const { isSuccess } =
      this.tagItemVendorService.addTaggedGroup(tempTagGroup);
  }

  public onSelectExistingItems() {
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }

    const itemNumbersForCurrentPE =
      this.form?.ItemList.filter(
        (item) => item.AddItems.PurchaseExecutive === this.loggedInUsername
      ).map((item) => item.AddItems.displayItemNumber) ?? [];

    const dialogData: SelectTableDialogData = {
      type: SelectTableDialogType.SELECT_ITEM_TABLE,
      title: 'Select From Existing Item',
      initialSelectedNumbers: _.cloneDeep(
        this.tagItemVendorService.tempTagGroupValue!.itemNumbers
      ),
      filterNumbers: itemNumbersForCurrentPE,
      actionButtonText: 'Add',
    };

    const dialogRef = this.dialog.open(SelectTableDialogComponent, {
      width: `${window.innerWidth - 150}px`,
      height: `750px`,
      maxHeight: `${window.innerHeight - 40}px`,
      data: dialogData,
    });

    this.dialogSubscription = dialogRef
      .afterClosed()
      .subscribe((result: SelectTableDialogResult) => {
        if (result?.data?.selectedNumbers) {
          const selectedItemNumbers = result.data.selectedNumbers;

          this.tagItemVendorService.updateTempTagGroup({
            itemNumbers: selectedItemNumbers,
          });

          // Remove non-selected numbers from checked array here
          this.checkedItemNumbers = this.checkedItemNumbers.filter(
            (itemNumber) => selectedItemNumbers.includes(itemNumber)
          );
        }
      });
  }

  public onItemTableSelectedChange(event: ItemNumbers) {
    this.checkedItemNumbers = event;
  }

  public onItemRowEdit(row: ItemFromTable) {
    alert(
      `Under Construction till next week... Should redirect to edit item page... Item Number clicked = ${row.displayItemNumber}`
    );
  }

  public onItemRowDelete(row: ItemFromTable) {
    const { displayItemNumber } = row;

    // Remove item from tempTagGroup
    const tagGroup = this.tagItemVendorService.tempTagGroupValue!;
    this.tagItemVendorService.updateTempTagGroup({
      itemNumbers: tagGroup.itemNumbers.filter(
        (itemNumber) => itemNumber !== displayItemNumber
      ),
    });

    // Remove item from checked
    this.checkedItemNumbers = this.checkedItemNumbers.filter(
      (itemNumber) => itemNumber !== displayItemNumber
    );
  }

  public onRemoveSelectedItems() {
    if (this.checkedItemNumbers.length === 0) {
      return;
    }
    const tagGroup = this.tagItemVendorService.tempTagGroupValue!;
    // Remove checked items from tempTagGroup
    this.tagItemVendorService.updateTempTagGroup({
      itemNumbers: tagGroup.itemNumbers.filter(
        (itemNumber) => !this.checkedItemNumbers.includes(itemNumber)
      ),
    });

    // Remove all items from checked
    this.checkedItemNumbers = [];
  }

  public onHasVendorTagChange(event: MatButtonToggleChange) {
    const value = event.value;

    if (value === HasVendorTag.YES) {
      this.tagItemVendorService.updateTempTagGroup({
        vendors: [],
        sourcingStatusId: SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED,
      });
    } else if (value === HasVendorTag.NO) {
      this.tagItemVendorService.updateTempTagGroup({
        vendors: null,
        sourcingStatusId: undefined,
      });

      // Clear checked vendors when not vendor tagging
      this.checkedVendorNumbers = [];
    }
  }

  public onSelectExistingVendors() {
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }

    const dialogData: SelectTableDialogData = {
      type: SelectTableDialogType.SELECT_VENDOR_TABLE,
      title: 'Select From Existing Vendor',
      initialSelectedStrings: _.cloneDeep(
        this.tagItemVendorService.tempTagGroupValue!.vendors!.map(
          (vendor) => vendor.vendorNumber
        )
      ),
      actionButtonText: 'Add',
    };

    const dialogRef = this.dialog.open(SelectTableDialogComponent, {
      width: `${window.innerWidth - 150}px`,
      height: `750px`,
      maxHeight: `${window.innerHeight - 40}px`,
      data: dialogData,
    });

    this.dialogSubscription = dialogRef
      .afterClosed()
      .subscribe((result: SelectTableDialogResult) => {
        if (result?.data?.selectedStrings) {
          const selectedVendorNumbers = result.data.selectedStrings;
          const selectedVendors =
            this.form?.VendorsToSource.filter(
              (vendor) =>
                vendor.VendorNumber &&
                !vendor.isDeleted &&
                !vendor.Strikethrough &&
                selectedVendorNumbers.includes(vendor.VendorNumber)
            ).map((vendor) => ({
              vendorName: vendor.Name ?? '',
              vendorNumber: vendor.VendorNumber!,
            })) ?? [];

          this.tagItemVendorService.updateTempTagGroup({
            vendors: selectedVendors,
          });

          // Remove non-selected numbers from checked array here
          this.checkedVendorNumbers = this.checkedVendorNumbers.filter(
            (vendorNumber) => selectedVendorNumbers.includes(vendorNumber!)
          );
        }
      });
  }

  public onVendorTableSelectedChange(event: NonNullVendorNumbers) {
    this.checkedVendorNumbers = event;
  }

  public onVendorRowDelete(row: VendorFromTable) {
    const { vendorNumber } = row;

    // Remove vendor from tempTagGroup
    const tagGroup = this.tagItemVendorService.tempTagGroupValue!;
    this.tagItemVendorService.updateTempTagGroup({
      vendors: tagGroup.vendors!.filter(
        (vendor) => vendor.vendorNumber !== vendorNumber
      ),
    });

    // Remove vendor from checked
    this.checkedVendorNumbers = this.checkedVendorNumbers.filter(
      (checkedVendorNumber) => checkedVendorNumber !== vendorNumber
    );
  }

  public onRemoveSelectedVendors() {
    if (this.checkedVendorNumbers.length === 0) {
      return;
    }

    // Remove checked vendors from tempTagGroup
    const tagGroup = this.tagItemVendorService.tempTagGroupValue!;

    this.tagItemVendorService.updateTempTagGroup({
      vendors: tagGroup.vendors!.filter(
        (vendor) => !this.checkedVendorNumbers.includes(vendor.vendorNumber)
      ),
    });

    // Remove all vendors from checked
    this.checkedVendorNumbers = [];
  }

  public onSourcingStatusChange(option: SourcingStatusOption) {
    this.tagItemVendorService.updateTempTagGroup({
      sourcingStatusId: option.id,
    });
  }

  public openVendorSearch(vendorSearchType: VendorSearchType) {

    if (vendorSearchType === VendorSearchType.NORMAL){
      const dialogData: VendorSearchDialogData = {
        vendorActionType: "",
        routeParams: this.route.snapshot.params
      }
  
      const dialogRef = this.dialog.open(VendorSearchDialogComponent, {
        width: `${window.innerWidth - 150}px`,
        height: `750px`,
        maxHeight: `${window.innerHeight - 40}px`,
        maxWidth: `1240px`,
        data: dialogData,
      });
  
    } else if (vendorSearchType === VendorSearchType.ASL) {
      const dialogData: AslVendorSearchDialogData = {
        showPdpMessage: !!this.form?.PDPComplianceItemNumbers 
      }
  
      const dialogRef = this.dialog.open(AslVendorSearchDialogComponent, {
        width: `${window.innerWidth - 150}px`,
        height: `750px`,
        maxHeight: `${window.innerHeight - 40}px`,
        maxWidth: `1240px`,
        data: dialogData,
      });
    }
  }

  public getFilterVendorNumbers(vendors: TaggedGroup['vendors']) {
    if (!vendors) {
      return;
    }

    return vendors.map((vendor) => vendor.vendorNumber);
  }

  setHeaderPath(purchaseType: string) {
    if (this.mpNumber && this.mpNumber != 'draft') {
      this.breadCrumbFlow.push(this.mpNumber);
    } else {
      switch (purchaseType) {
        case 'rfq':
          this.currentRoute.push('Request For Quotation');
          this.breadCrumbFlow.push('Request For Quotation');
          break;
        case 'tender':
          this.currentRoute.push('Tender');
          this.breadCrumbFlow.push('Tender');
          break;
        case 'corporatecard':
          this.currentRoute.push('Corporate Credit Card');
          this.breadCrumbFlow.push('Corporate Credit Card');
          break;
        case 'dc':
          this.currentRoute.push('Direct Contracting');
          this.breadCrumbFlow.push('Direct Contracting');
          break;
        case 'permissiblecoveringpurchaseorder':
          this.currentRoute.push('Permissible Reasons');
          this.breadCrumbFlow.push('Permissible Reasons');
          break;
      }
    }
  }

  public get SelectItemTableAction(): typeof SelectItemTableAction {
    return SelectItemTableAction;
  }

  public get SelectVendorTableAction(): typeof SelectVendorTableAction {
    return SelectVendorTableAction;
  }

  public get HasVendorTag(): typeof HasVendorTag {
    return HasVendorTag;
  }

  public get VendorSearchType(): typeof VendorSearchType {
    return VendorSearchType;
  }

  public get SourcingStatusId(): typeof SourcingStatusId {
    return SourcingStatusId;
  }
}
